import React from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { AgentCardsFilterType, AgentMarketPlaceItemWithFilter } from './AgentMarketPlace';

interface FilterSection {
  title: string;
  id: AgentCardsFilterType;
  items: string[];
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
}

interface AgentCardsFilterProps {
  agentCardsFilterSections: FilterSection[];
  agentList: AgentMarketPlaceItemWithFilter[];
  agentListLoading: boolean;
  onPageChange: (page: number) => void;
}

const AgentCardsFilter: React.FC<AgentCardsFilterProps> = ({
  agentCardsFilterSections,
  agentList,
  agentListLoading,
  onPageChange,
}) => {
  return (
    <>
      {/* Filter Dropdowns in Row */}
      {!agentListLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          {agentCardsFilterSections.map((section) => (
            <FormControl
              key={section.id}
              variant="outlined"
              sx={{
                minWidth: { xs: 375, sm: 235 },
                maxWidth: { xs: 375, sm: 235 },
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '50px',
                  '& fieldset': {
                    borderRadius: '8px',
                  },
                },
                '& .MuiInputLabel-root': {
                  top: '-3px',
                  '&.Mui-focused': {
                    top: '-3px',
                  },
                },
              }}
            >
              <InputLabel id={`${section.id}-label`}>Filter by {section.title}</InputLabel>
              <Select
                labelId={`${section.id}-label`}
                id={section.id}
                multiple
                value={section.selectedFilters}
                onChange={(event) => {
                  const value = event.target.value as string[];
                  section.setSelectedFilters(value);
                  onPageChange(1);
                }}
                label={`Filter by ${section.title}`}
                renderValue={(selected) => {
                  if (selected.length === 0) return '';
                  return (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {selected.join(', ')}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      marginTop: 8,
                      borderRadius: '8px',
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {section.items.sort().map((item) => (
                  <MenuItem key={item} value={item}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{item}</Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        (
                        {
                          section.id === AgentCardsFilterType.CloudProvider && item.toLowerCase() === 'google'
                            ? (() => {
                              const googleAgents = agentList.filter(agent => agent.cloud_provider.toLowerCase() === 'google');
                              const agentsCount = googleAgents.length;
                              // If agents property exists, sum it
                              const subagentsCount = googleAgents.reduce((acc, agent) => acc + (agent.agents || 0), 0);
                              return subagentsCount > 0 ? `${agentsCount}/${subagentsCount}` : agentsCount;
                            })()
                            : agentList.filter((agent) =>
                              section.id === AgentCardsFilterType.Category
                                ? agent.category === item
                                : section.id === AgentCardsFilterType.Role
                                  ? agent.role === item
                                  : section.id === AgentCardsFilterType.CloudProvider
                                    ? agent.cloud_provider === item
                                    : false
                            ).length
                        }
                        )
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>
      )}
    </>
  );
};

export default AgentCardsFilter;
